class AboutUsGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-au-track]');
    if (!this.track) return;

    this.slides = Array.from(this.track.querySelectorAll('[data-au-slide]'));
    if (this.slides.length === 0) return;

    this.prevBtn = this.querySelector('[data-au-prev]');
    this.nextBtn = this.querySelector('[data-au-next]');
    this.dotsContainer = this.querySelector('[data-au-dots]');

    this.autoplay = this.dataset.autoplay === 'true';
    this.autoplayDelay = Number(this.dataset.autoplayDelay) || 5000;
    this.loop = this.dataset.loop !== 'false';

    this.currentIndex = 0;
    this.autoplayTimer = null;

    this.#buildDots();
    this.#bindEvents();
    this.#updateActiveDot();

    if (this.autoplay && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.#startAutoplay();
      this.addEventListener('pointerenter', () => this.#stopAutoplay());
      this.addEventListener('pointerleave', () => this.#startAutoplay());
      this.addEventListener('focusin', () => this.#stopAutoplay());
      this.addEventListener('focusout', () => this.#startAutoplay());
    }

    this.#observeScroll();
  }

  disconnectedCallback() {
    this.#stopAutoplay();
    this.scrollObserver?.disconnect();
  }

  #buildDots() {
    if (!this.dotsContainer) return;
    this.dotsContainer.innerHTML = '';
    this.slides.forEach((_, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'about-us__gallery-dot';
      dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
      dot.addEventListener('click', () => this.#goTo(i));
      this.dotsContainer.appendChild(dot);
    });
    this.dots = Array.from(this.dotsContainer.children);
  }

  #bindEvents() {
    this.prevBtn?.addEventListener('click', () => this.#step(-1));
    this.nextBtn?.addEventListener('click', () => this.#step(1));
  }

  #observeScroll() {
    if (!('IntersectionObserver' in window)) return;
    this.scrollObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.intersectionRatio >= 0.5) {
            const i = this.slides.indexOf(entry.target);
            if (i !== -1) {
              this.currentIndex = i;
              this.#updateActiveDot();
            }
          }
        });
      },
      { root: this.track, threshold: [0.5] }
    );
    this.slides.forEach((slide) => this.scrollObserver.observe(slide));
  }

  #step(delta) {
    let next = this.currentIndex + delta;
    if (next < 0) next = this.loop ? this.slides.length - 1 : 0;
    if (next >= this.slides.length) next = this.loop ? 0 : this.slides.length - 1;
    this.#goTo(next);
  }

  #goTo(index) {
    const slide = this.slides[index];
    if (!slide) return;
    this.currentIndex = index;
    this.track.scrollTo({
      left: slide.offsetLeft - this.track.offsetLeft,
      behavior: 'smooth',
    });
    this.#updateActiveDot();
  }

  #updateActiveDot() {
    if (!this.dots) return;
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === this.currentIndex);
      dot.setAttribute('aria-current', i === this.currentIndex ? 'true' : 'false');
    });
  }

  #startAutoplay() {
    if (!this.autoplay) return;
    this.#stopAutoplay();
    this.autoplayTimer = setInterval(() => this.#step(1), this.autoplayDelay);
  }

  #stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }
}

if (!customElements.get('about-us-gallery')) {
  customElements.define('about-us-gallery', AboutUsGallery);
}
